/**
 * Product-related type definitions
 */
import { Timestamp } from 'firebase/firestore';

export enum ProductCategory {
  SUCTION_TIPS = 'suction_tips',
  RETAINER_BOX = 'retainer_box',
  DENTURE_BOX = 'denture_box',
  INSTRUMENT_TRAY = 'instrument_tray',
  IMPRESSION = 'impression',
  HYGIENE_DISPOSABLE = 'hygiene_disposable',
  HANDPIECES = 'handpieces',
  IMPLANT = 'implant',
  ENDODONTIC = 'endodontic',
  PEDODONTICS = 'pedodontics',
  BURS = 'burs',
  CROWNS = 'crowns',
  STERILIZATION = 'sterilization',
  WATER_FLOSSER = 'water_flosser',
  DISPOSABLES = 'disposables',
}

export const ProductCategoryLabels: Record<ProductCategory, string> = {
  [ProductCategory.SUCTION_TIPS]: 'Suction Tips',
  [ProductCategory.RETAINER_BOX]: 'Retainer Box',
  [ProductCategory.DENTURE_BOX]: 'Denture Box',
  [ProductCategory.INSTRUMENT_TRAY]: 'Instrument Tray',
  [ProductCategory.IMPRESSION]: 'Impression Materials',
  [ProductCategory.HYGIENE_DISPOSABLE]: 'Hygiene & Disposables',
  [ProductCategory.HANDPIECES]: 'Handpieces',
  [ProductCategory.IMPLANT]: 'Implants',
  [ProductCategory.ENDODONTIC]: 'Endodontic',
  [ProductCategory.PEDODONTICS]: 'Pedodontics',
  [ProductCategory.BURS]: 'Burs',
  [ProductCategory.CROWNS]: 'Crowns',
  [ProductCategory.STERILIZATION]: 'Sterilization',
  [ProductCategory.WATER_FLOSSER]: 'Water Flosser',
  [ProductCategory.DISPOSABLES]: 'Disposables',
};

export enum Brand {
  SUNVIVO = 'Sunvivo',
  DEXELL = 'Dexell',
  HAGEN = 'Hagen',
  THREE_STAR = '3Star',
  NEXEN = 'Nexen',
  PROXEE = 'Proxee',
  MANI = 'Mani',
  DIADENT = 'Diadent',
  MAILYARD = 'Mailyard',
  STAR = 'Star',
}

export const BrandLabels: Record<Brand, string> = {
  [Brand.SUNVIVO]: 'Sunvivo',
  [Brand.DEXELL]: 'Dexell',
  [Brand.HAGEN]: 'Hagen',
  [Brand.THREE_STAR]: '3Star',
  [Brand.NEXEN]: 'Nexen',
  [Brand.PROXEE]: 'Proxee',
  [Brand.MANI]: 'Mani',
  [Brand.DIADENT]: 'Diadent',
  [Brand.MAILYARD]: 'Mailyard',
  [Brand.STAR]: 'Star',
};

export interface ProductSpecification {
  label: string;
  value: string;
}

export interface CrownSizeConfig {
  quadrants: { id: string; label: string; shortLabel: string }[];
  toothTypes: { id: string; label: string; notation: string }[];
  sizes: number[];
}

export interface SizeMatrixSection {
  id: string;
  label: string;
  shortLabel?: string;
  rows: { id: string; label: string; notation: string }[];
  columns: (string | number)[];
}

/**
 * Generic, Firestore-serializable config that drives the SizeMatrixSelector grid.
 * The cell key is built declaratively (no functions) as:
 *   (includeSectionInKey && shortLabel ? shortLabel + sectionSeparator : '')
 *     + row.notation + columnPrefix + column
 * (the columnPrefix is skipped when column === '').
 */
export interface SizeMatrixConfig {
  /** Heading shown above the grid, e.g. "Select K-File Sizes" */
  title: string;
  /** Singular noun for the summary/toast, e.g. "file" / "point" / "crown" */
  unitLabel: string;
  /** CSS columns used to lay out sections (2 = crown 2x2, 1 = stacked). Default 1. */
  gridColumns?: number;
  /** String inserted between row.notation and column in the key (doubles as row/column separator). Default ''. */
  columnPrefix?: string;
  /** String between section shortLabel and the rest of the key. Default '-'. */
  sectionSeparator?: string;
  /** Whether section.shortLabel is prefixed into the key. Default false. */
  includeSectionInKey?: boolean;
  /** Whether each section renders its label/short-label header. Default gridColumns > 1. */
  showSectionHeader?: boolean;
  sections: SizeMatrixSection[];
}

export interface Product {
  id: string;
  name: string;
  nameLowercase: string;
  slug: string;
  description: string;
  shortDescription?: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  category: ProductCategory;
  brand: Brand;
  images: string[];
  thumbnail: string;
  specifications: ProductSpecification[];
  moq: number;
  inStock: boolean;
  stockQuantity: number;
  colors?: string[];
  sizes?: string[];
  sku: string;
  isFeatured: boolean;
  isActive: boolean;
  tags?: string[];
  metaTitle?: string;
  metaDescription?: string;
  crownSizeConfig?: CrownSizeConfig;
  sizeMatrixConfig?: SizeMatrixConfig;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ProductFilters {
  category?: ProductCategory;
  brand?: Brand;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  isFeatured?: boolean;
  search?: string;
  tags?: string[];
  sortBy?: 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc' | 'newest' | 'popular';
}

export interface ProductFormData {
  name: string;
  description: string;
  shortDescription?: string;
  price: number;
  originalPrice?: number;
  category: ProductCategory;
  brand: Brand;
  images: (File | string)[];
  specifications: ProductSpecification[];
  moq: number;
  stockQuantity: number;
  colors?: string[];
  sizes?: string[];
  sku: string;
  isFeatured: boolean;
  isActive: boolean;
  tags?: string[];
}
